import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Grid,
  Checkbox,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { AttachMoney, CheckBox, CheckBoxOutlineBlank, Delete, Edit, FavoriteBorder, OpenInNew } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import HouseCollectionEmptyState from '../components/HouseCollectionEmptyState';
import HouseCollectionGrid from '../components/HouseCollectionGrid';
import HouseCollectionHeader from '../components/HouseCollectionHeader';
import Layout from '../components/Layout';
import HousePageBackLink from '../components/HousePageBackLink';
import axios from '../api/axios';
import { formatPrice } from '../utils/format';

interface WishlistItem {
  id: string;
  title: string;
  description?: string;
  estimatedPrice?: number;
  link?: string;
  isPurchased: boolean;
}

interface WishlistPageProps {
  embedded?: boolean;
  isOwner?: boolean;
}

export const WishlistPage: React.FC<WishlistPageProps> = ({ embedded = false, isOwner = false }) => {
  const { id: houseId } = useParams<{ id: string }>();
  const [items, setItems] = useState<WishlistItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<WishlistItem | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    estimatedPrice: '',
    url: '',
  });
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage || i18n.language;

  const fetchItems = useCallback(async () => {
    if (!houseId) {
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(`/wishlist/house/${houseId}`);
      setItems(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || t('wishlist.failedFetch'));
    } finally {
      setLoading(false);
    }
  }, [houseId, t]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (houseId) {
      fetchItems();
    }
  }, [houseId, fetchItems]);

  const handleCreate = () => {
    setSelectedItem(null);
    setFormData({ title: '', description: '', estimatedPrice: '', url: '' });
    setDialogOpen(true);
  };

  const handleEdit = (item: WishlistItem) => {
    setSelectedItem(item);
    setFormData({
      title: item.title,
      description: item.description || '',
      estimatedPrice: item.estimatedPrice?.toString() || '',
      url: item.link || '',
    });
    setDialogOpen(true);
  };

  const handleDelete = async (itemId: string) => {
    if (!window.confirm(t('wishlist.deleteConfirm'))) return;

    try {
      await axios.delete(`/wishlist/${itemId}`);
      fetchItems();
    } catch (err: any) {
      setError(err.response?.data?.message || t('wishlist.failedDelete'));
    }
  };

  const handleTogglePurchased = async (itemId: string) => {
    try {
      await axios.patch(`/wishlist/${itemId}/toggle-purchased`);
      fetchItems();
    } catch (err: any) {
      setError(err.response?.data?.message || t('wishlist.failedUpdate'));
    }
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        title: formData.title,
        description: formData.description || undefined,
        estimatedPrice: formData.estimatedPrice ? parseFloat(formData.estimatedPrice) : undefined,
        link: formData.url || undefined,
        houseId,
      };

      if (selectedItem) {
        await axios.put(`/wishlist/${selectedItem.id}`, payload);
      } else {
        await axios.post('/wishlist', payload);
      }

      setDialogOpen(false);
      fetchItems();
    } catch (err: any) {
      setError(err.response?.data?.message || t('wishlist.failedSave'));
    }
  };

  const content = (
    <Container maxWidth={embedded ? false : "lg"} sx={{ px: embedded ? 0 : undefined }}>
      {!embedded && <HousePageBackLink />}
      <HouseCollectionHeader
        title={t('wishlist.title')}
        description={t('wishlist.noItemsDescription')}
        actionLabel={isOwner ? t('wishlist.addItem') : undefined}
        onAction={isOwner ? handleCreate : undefined}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : items.length === 0 ? (
        <HouseCollectionEmptyState
          title={t('wishlist.noItems')}
          description={t('wishlist.noItemsDescription')}
          actionLabel={isOwner ? t('wishlist.addFirst') : undefined}
          onAction={isOwner ? handleCreate : undefined}
        />
      ) : (
        <HouseCollectionGrid
          spacing={2}
        >
          {items.map((item) => (
            <Grid item xs={8} sm={6} md={4} key={item.id} sx={{ flexShrink: 0, scrollSnapAlign: 'start' }}>
              <Card sx={{ opacity: item.isPurchased ? 0.6 : 1, height: '100%', border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, pr: 1 }}>
                      <FavoriteBorder fontSize="small" color="primary" />
                      <Typography variant="h6" sx={{ minWidth: 0 }}>
                        {item.title}
                      </Typography>
                    </Stack>
                    {item.isPurchased && <Chip label={t('common.statuses.purchased')} color="success" size="small" />}
                  </Box>
                  {item.description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 1,
                        minHeight: 58,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {item.description}
                    </Typography>
                  )}
                  {item.estimatedPrice && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <AttachMoney fontSize="small" sx={{ color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {formatPrice(item.estimatedPrice, language)}
                      </Typography>
                    </Box>
                  )}
                  {item.link && (
                    <Button
                      size="small"
                      href={item.link}
                      target="_blank"
                      endIcon={<OpenInNew fontSize="inherit" />}
                      sx={{ mt: 1 }}
                    >
                      {t('wishlist.viewProduct')}
                    </Button>
                  )}
                </CardContent>
                <CardActions sx={{ justifyContent: 'space-between', px: { xs: 1.5, sm: 2 }, pb: { xs: 1.5, sm: 2 }, pt: 0 }}>
                  {!isOwner ? (
                    <Checkbox
                      checked={item.isPurchased}
                      onChange={() => handleTogglePurchased(item.id)}
                      icon={<CheckBoxOutlineBlank />}
                      checkedIcon={<CheckBox />}
                    />
                  ) : (
                    <Box />
                  )}
                  {isOwner && (
                    <Box>
                      <IconButton size="small" onClick={() => handleEdit(item)}>
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(item.id)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </CardActions>
              </Card>
            </Grid>
          ))}
        </HouseCollectionGrid>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedItem ? t('wishlist.editTitle') : t('wishlist.createTitle')}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label={t('common.fields.title')}
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              sx={{ mb: 2 }}
              required
            />
            <TextField
              fullWidth
              label={t('common.fields.description')}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={3}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label={t('common.labels.estimatedPrice')}
              value={formData.estimatedPrice}
              onChange={(e) => setFormData({ ...formData, estimatedPrice: e.target.value })}
              type="number"
              inputProps={{ step: '0.01' }}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label={t('common.labels.productUrl')}
              value={formData.url}
              onChange={(e) => setFormData({ ...formData, url: e.target.value })}
              helperText={t('common.helpers.productUrlOptional')}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t('common.actions.cancel')}</Button>
          <Button onClick={handleSubmit} variant="contained">
            {selectedItem ? t('common.actions.update') : t('common.actions.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );

  if (embedded) {
    return content;
  }

  return (
    <Layout title={t('wishlist.layoutTitle')}>
      {content}
    </Layout>
  );
};

export default WishlistPage;
